/**
 * Export Controls Component
 * UI controls for exporting reports in various formats
 */

import React, { useState } from 'react';
import { 
  ArrowDownTrayIcon, 
  PrinterIcon, 
  DocumentArrowDownIcon,
  TableCellsIcon,
  DocumentTextIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline';
import { useReportExport, usePrintReport, useReportDownload } from '../hooks/useReportExport';
import { REPORT_FORMATS } from '../../reports.generators/types/reportTypes';

const ExportControls = ({ 
  content, 
  reportType, 
  parameters = {}, 
  className = '',
  showLabels = true,
  buttonSize = 'md',
  orientation = 'horizontal'
}) => {
  const [selectedFormat, setSelectedFormat] = useState(REPORT_FORMATS.PDF);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const { exportReport, exporting, exportError, exportProgress, clearError } = useReportExport();
  const { print, printing, printError } = usePrintReport();
  const { 
    downloadPDF, 
    downloadCSV, 
    downloadHTML, 
    downloadJSON,
    downloading, 
    downloadError 
  } = useReportDownload();

  const handleExport = async (format) => {
    clearError();
    
    const options = {
      format,
      download: true
    };

    await exportReport(content, reportType, parameters, options);
    setShowDropdown(false);
  };

  const handlePrint = async () => {
    await print(content, reportType, {
      printStyles: true,
      pageBreaks: true,
      headerFooter: true
    });
  };

  const handleQuickDownload = async (format) => {
    switch (format) {
      case REPORT_FORMATS.PDF:
        await downloadPDF(content, reportType, parameters);
        break;
      case REPORT_FORMATS.CSV:
        await downloadCSV(content, reportType, parameters);
        break;
      case REPORT_FORMATS.HTML:
        await downloadHTML(content, reportType, parameters);
        break;
      case REPORT_FORMATS.JSON:
        await downloadJSON(content, reportType, parameters);
        break;
      default:
        console.warn('Unknown export format:', format);
        break;
    }
  };

  const formatIcons = {
    [REPORT_FORMATS.PDF]: DocumentArrowDownIcon,
    [REPORT_FORMATS.CSV]: TableCellsIcon,
    [REPORT_FORMATS.HTML]: DocumentTextIcon,
    [REPORT_FORMATS.JSON]: CodeBracketIcon
  };

  const formatLabels = {
    [REPORT_FORMATS.PDF]: 'PDF',
    [REPORT_FORMATS.CSV]: 'CSV',
    [REPORT_FORMATS.HTML]: 'HTML',
    [REPORT_FORMATS.JSON]: 'JSON'
  };

  const getButtonClasses = (variant = 'primary') => {
    const baseClasses = 'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500';
    
    const sizeClasses = {
      sm: 'px-2 py-1 text-xs',
      md: 'px-3 py-2 text-sm',
      lg: 'px-4 py-2 text-base'
    };

    const variantClasses = {
      primary: 'text-white bg-blue-600 hover:bg-blue-700 border border-transparent',
      secondary: 'text-gray-700 bg-white hover:bg-gray-50 border border-gray-300',
      outline: 'text-blue-600 bg-white hover:bg-blue-50 border border-blue-600'
    };

    return `${baseClasses} ${sizeClasses[buttonSize]} ${variantClasses[variant]}`;
  };

  const isLoading = exporting || printing || downloading;
  const hasError = exportError || printError || downloadError;

  return (
    <div className={`export-controls ${className}`}>
      {/* Error Display */}
      {hasError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
          <p className="text-sm">
            {exportError || printError || downloadError}
          </p>
        </div>
      )}

      {/* Progress Bar */}
      {exporting && exportProgress > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Exporting...</span>
            <span>{exportProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${exportProgress}%` }}
            />
          </div>
        </div>
      )}

      <div className={`flex ${orientation === 'vertical' ? 'flex-col space-y-2' : 'space-x-2'}`}>
        {/* Print Button */}
        <button
          onClick={handlePrint}
          disabled={isLoading}
          className={getButtonClasses('secondary')}
          title="Print Report"
        >
          <PrinterIcon className="h-4 w-4" />
          {showLabels && <span className="ml-2">Print</span>}
          {printing && <span className="ml-2">Printing...</span>}
        </button>

        {/* Quick Export Buttons */}
        <button
          onClick={() => handleQuickDownload(REPORT_FORMATS.PDF)}
          disabled={isLoading}
          className={getButtonClasses('primary')}
          title="Download as PDF"
        >
          <DocumentArrowDownIcon className="h-4 w-4" />
          {showLabels && <span className="ml-2">PDF</span>}
        </button>

        <button
          onClick={() => handleQuickDownload(REPORT_FORMATS.CSV)}
          disabled={isLoading}
          className={getButtonClasses('outline')}
          title="Download as CSV"
        >
          <TableCellsIcon className="h-4 w-4" />
          {showLabels && <span className="ml-2">CSV</span>}
        </button>

        {/* Export Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={isLoading}
            className={getButtonClasses('secondary')}
            title="More export options"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {showLabels && <span className="ml-2">Export</span>}
            <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-10">
              <div className="py-1">
                {Object.values(REPORT_FORMATS).map(format => {
                  const IconComponent = formatIcons[format];
                  return (
                    <button
                      key={format}
                      onClick={() => handleExport(format)}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      disabled={isLoading}
                    >
                      {IconComponent && <IconComponent className="h-4 w-4 mr-3" />}
                      Export as {formatLabels[format]}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Loading Overlay for the entire component */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-md">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">
              {exporting && 'Exporting...'}
              {printing && 'Printing...'}
              {downloading && 'Downloading...'}
            </span>
          </div>
        </div>
      )}

      {/* Click outside handler for dropdown */}
      {showDropdown && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default ExportControls;